import { BASE_API_URL } from '@/lib/constants';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import { ListingRequest, ListingResponse } from '../listing.types';

const URL = `${BASE_API_URL}/provider-search`;

const doctorsList = async (payload: ListingRequest): Promise<ListingResponse> => {
  const response = await fetch(URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
};

const QueryKeys = {
  all: ['doctors-list'] as const,
  list: (filters: ListingRequest) => ['doctors-list', filters] as const,
};

export const useDoctorsListing = (
  filters: ListingRequest,
  options?: UseQueryOptions<ListingResponse, Error>
): UseQueryResult<ListingResponse, Error> => {
  // Fix for inconsistent parameter naming
  // Decided that this is the best centralized place to handle it,
  // otherwise it is messy to control as the query param is named keywordExact,
  // but the request payload property is named keywordsExact
  const filtersWithFixedParamNames = {
    ...filters,
    keywordsExact: filters.keywordExact,
    // Query param is named 'keywords' but request payload property is named 'keyword'
    keyword: filters.keywords,
  };

  // We just delete those to avoid sending both versions and confusing the backend
  delete filtersWithFixedParamNames.keywords;
  delete filtersWithFixedParamNames.keywordExact;

  return useQuery({
    queryKey: QueryKeys.list(filtersWithFixedParamNames),
    queryFn: () => doctorsList(filtersWithFixedParamNames),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    placeholderData: (previousData) => previousData, // Keeps previous data while fetching new one
    refetchOnMount: false, // Don't refetch automatically when the component mounts
    refetchOnWindowFocus: false, // Avoid refetching when switching tabs
    ...options,
  });
};
